import moment from 'moment';
import Swal from "sweetalert2";
import HelperMixin from '../../../Shared/HelperMixin.vue';
import PaginationHelper from '../../../Shared/PaginationHelper.vue';

export default {
    name: 'Bookings',
    mixins: [HelperMixin],
    components: {
        PaginationHelper
    },

    async created() {
        this.loginDetails = await this.CheckLoginStatus();

        if (this.loginDetails) {
            if (this.loginDetails.userType != 1 && this.loginDetails.userType != 5)
                this.logout();
        } else {
            this.logout();
        }

        await Promise.all([
            this.GetAllRooms(),
            this.GetAllUsers(),
        ]);

        this.GetInfo(this.pageNo);
    },

    computed: {
        totalPages() {
            return Math.ceil(this.pages / this.pageSize);
        },

        // Picker options: only allow dates whose weekday matches selected schedule
        datePickerOptions() {
            const self = this;
            return {
                disabledDate(date) {
                    if (!self.SelectedScheduleDayOfWeek && self.SelectedScheduleDayOfWeek !== 0)
                        return false;
                    // DayOfWeekEnum: 0=Sunday … 6=Saturday (matches JS getDay())

                    return date.getDay() !== self.SelectedScheduleDayOfWeek;
                }
            };
        }
    },

    filters: {
        moment: function (date) {
            if (!date) return 'فارغ';
            return moment(date).format('DD/MM/YYYY');
        },
        momentTime: function (date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        dayName: function (value) {
            const days = { 0: 'الأحد', 1: 'الإثنين', 2: 'الثلاثاء', 3: 'الأربعاء', 4: 'الخميس', 5: 'الجمعة', 6: 'السبت' };
            return days[value] ?? value;
        }
    },

    data() {
        return {
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            Search: '',

            // Filters
            FilterRoomId:     null,
            FilterScheduleId: null,
            FilterStatus:     null,
            FilterDate:       null,

            ScreenTitle:       'الحجوزات',
            ScreenTitleSingle: 'حجز',
            ScreenTitleSingleAl: 'الحجز',

            Info:       [],
            Statistics: [],

            // Dropdown data
            RoomsList:         [],
            SchedulesList:     [],   // for table filter (depends on FilterRoomId)
            FormSchedulesList: [],   // for add/edit form (depends on ruleForm.RoomId)
            UsersList:         [],

            // Availability feedback
            AvailabilityMsg: '',
            AvailabilityOk:  true,

            // Holds the DayOfWeek of the chosen schedule – used to disable invalid dates
            SelectedScheduleDayOfWeek: null,

            ruleForm: {
                Id:             '',
                UserId:         '',
                RoomId:         '',
                RoomScheduleId: '',
                BookingDate:    '',
                Notes:          '',
            },

            rules: {
                UserId:         this.$helper.Required(),
                RoomId:         this.$helper.Required(),
                RoomScheduleId: this.$helper.Required(),
                BookingDate:    this.$helper.Required(),
            },
        };
    },

    methods: {

        // ------------------------------------------------------------------
        //  Data loaders
        // ------------------------------------------------------------------
        GetInfo(pageNo) {
            this.pageNo = pageNo;
            if (this.pageNo === undefined) this.pageNo = 1;
            console.log("dfd", this.FilterDate)
            if (!this.Search) this.$blockUI.Start();
            this.$http.GetBookings(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.FilterRoomId     || '',
                this.FilterScheduleId || '',
                this.FilterStatus     || '',
                this.FilterDate       || ''
            )
                .then(response => {
                    if (!this.Search) this.$blockUI.Stop();
                    this.Info       = response.data.info;
                    this.pages      = response.data.count;
                    this.Statistics = response.data.statistics;
                })
                .catch(() => {
                    if (!this.Search) this.$blockUI.Stop();
                    this.pages = 0;
                });
        },

        async GetAllRooms() {
            try {
                const res = await this.$http.GetAllRooms();
                this.RoomsList = res.data.info || res.data;
            } catch {
                this.RoomsList = [];
            }
        },

        async GetAllUsers() {
            try {
                const res = await this.$http.GetAllInstructors();
                this.UsersList = res.data.info || res.data;
            } catch {
                this.UsersList = [];
            }
        },

        async LoadSchedulesForRoom(roomId, targetList) {
            if (!roomId) {
                this[targetList] = [];
                return;
            }
            try {
                const res = await this.$http.GetSchedulesByRoom(roomId);
                const raw = res.data.info || res.data;
                const dayNames = { 0: 'الأحد', 1: 'الإثنين', 2: 'الثلاثاء', 3: 'الأربعاء', 4: 'الخميس', 5: 'الجمعة', 6: 'السبت' };
                this[targetList] = raw.map(s => ({
                    id:        s.id,
                    label:     `${dayNames[s.dayOfWeek]} | ${s.startTime} - ${s.endTime}`,
                    dayOfWeek: s.dayOfWeek,
                }));
            } catch {
                this[targetList] = [];
            }
        },

        // ------------------------------------------------------------------
        //  Filter-bar room change → reload filter schedules list
        // ------------------------------------------------------------------
        async OnRoomFilterChange() {
            this.FilterScheduleId = null;
            await this.LoadSchedulesForRoom(this.FilterRoomId, 'SchedulesList');
            this.GetInfo();
        },

        // ------------------------------------------------------------------
        //  Form room change → reload form schedules list, reset schedule
        // ------------------------------------------------------------------
        async OnRoomChange() {
            this.ruleForm.RoomScheduleId  = '';
            this.SelectedScheduleDayOfWeek = null;
            this.ruleForm.BookingDate      = '';
            this.AvailabilityMsg           = '';
            await this.LoadSchedulesForRoom(this.ruleForm.RoomId, 'FormSchedulesList');
        },

        // ------------------------------------------------------------------
        //  Schedule selected → set day filter & check availability
        // ------------------------------------------------------------------
        OnScheduleChange() {
            this.ruleForm.BookingDate = '';
            this.AvailabilityMsg      = '';
            const sch = this.FormSchedulesList.find(s => s.id === this.ruleForm.RoomScheduleId);
            this.SelectedScheduleDayOfWeek = sch ? sch.dayOfWeek : null;
            this.CheckAvailability();
        },

        CheckAvailability() {

            if (!this.ruleForm.RoomScheduleId || !this.ruleForm.BookingDate) {
                this.AvailabilityMsg = '';
                return;
            }
            this.$http.CheckBookingAvailability(this.ruleForm.RoomScheduleId, this.ruleForm.BookingDate, this.ruleForm.Id || 0)
                .then(res => {
                    this.AvailabilityOk  = res.data.available;
                    this.AvailabilityMsg = res.data.message;
                })
                .catch(() => {
                    this.AvailabilityMsg = '';
                });
        },

        // ------------------------------------------------------------------
        //  Add
        // ------------------------------------------------------------------
        submitForm(formName) {
            this.$refs[formName].validate((valid) => {
                if (valid) {
                    this.ruleForm.Id = 0;
                    this.$blockUI.Start();
                    this.$http.AddBooking(this.ruleForm)
                        .then(response => {
                            this.$blockUI.Stop();
                            this.GetInfo(this.pageNo);
                            this.ClearForm(this.ruleForm);
                            this.AvailabilityMsg = '';
                            this.$helper.DestroyElemntById('Close');
                            this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                        })
                        .catch((err) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('error', 'خطأ بعملية الإضافة',
                                err.response?.data || 'حدث خطأ غير متوقع');
                        });
                } else {
                    this.$helper.showSwal('warning');
                    return false;
                }
            });
        },

        // ------------------------------------------------------------------
        //  Open Edit
        // ------------------------------------------------------------------
        async OpenEditDialog(item) {
            this.ruleForm.Id             = item.id;
            this.ruleForm.UserId         = item.userId;
            this.ruleForm.RoomId         = item.roomId;
            this.ruleForm.RoomScheduleId = item.roomScheduleId;
            this.ruleForm.BookingDate    = item.bookingDate ? item.bookingDate.substring(0, 10) : '';
            this.ruleForm.Notes          = item.notes;
            this.AvailabilityMsg         = '';

            await this.LoadSchedulesForRoom(item.roomId, 'FormSchedulesList');
            const sch = this.FormSchedulesList.find(s => s.id === item.roomScheduleId);
            this.SelectedScheduleDayOfWeek = sch ? sch.dayOfWeek : null;
        },

        // ------------------------------------------------------------------
        //  Edit submit
        // ------------------------------------------------------------------
        submitEditForm(formName) {
            this.$refs[formName].validate((valid) => {
                if (valid) {
                    this.ruleForm.Id = Number(this.ruleForm.Id);
                    this.$blockUI.Start();
                    this.$http.EditBooking(this.ruleForm)
                        .then(response => {
                            this.$blockUI.Stop();
                            this.GetInfo(this.pageNo);
                            this.ClearForm(this.ruleForm);
                            this.AvailabilityMsg = '';
                            this.$helper.DestroyElemntById('CloseEdit');
                            this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                        })
                        .catch((err) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('error', 'خطأ بعملية التعديل',
                                err.response?.data || 'حدث خطأ غير متوقع');
                        });
                } else {
                    this.$helper.showSwal('warning');
                    return false;
                }
            });
        },

        // ------------------------------------------------------------------
        //  Cancel booking
        // ------------------------------------------------------------------
        CancelBooking(Id) {
            Swal.fire({
                title: 'تأكيد الإلغاء',
                text:  'هل أنت متأكد من إلغاء هذا الحجز؟',
                icon:  'warning',
                showCancelButton:  true,
                customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling:    false,
                confirmButtonText: 'نعم، إلغاء الحجز',
                cancelButtonText:  'تراجع',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$blockUI.Start();
                    this.$http.CancelBooking(Id)
                        .then((response) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                            this.GetInfo(this.pageNo);
                        })
                        .catch((err) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('error', 'خطأ بعملية الإلغاء',
                                err.response?.data || 'حدث خطأ غير متوقع');
                        });
                }
            });
        },

        // ------------------------------------------------------------------
        //  Delete
        // ------------------------------------------------------------------
        Delete(Id) {
            Swal.fire({
                title: 'تأكيد العملية',
                text:  'هل أنت متأكد من عملية الحذف؟',
                icon:  'question',
                customClass: { confirmButton: 'btn btn-primary' },
                buttonsStyling:    false,
                confirmButtonText: 'تأكيد العملية',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$blockUI.Start();
                    this.$http.DeleteBooking(Id)
                        .then((response) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('success', 'عملية ناجحة', response.data);
                            this.GetInfo(this.pageNo);
                        })
                        .catch((err) => {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('error', 'خطأ بعملية الحذف',
                                err.response?.data || 'حدث خطأ غير متوقع');
                        });
                }
            });
        },

    }
};
